import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import { ReservationReturn } from '../clients/rust-api-client';
import { memberCommentService, memberReservationService } from '../clients/ApiClients';
import AppSidebar from '../AppSidebar';
import { Navbar } from '../Components/Catalyst/navbar';
import { SidebarLayout } from '../Components/Catalyst/sidebar-layout';
import { Heading, Subheading } from '../Components/Catalyst/heading';
import { Text } from '../Components/Catalyst/text';
import { Badge } from '../Components/Catalyst/badge';
import { Button } from '../Components/Catalyst/button';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import { ErrorPage } from '../Components/Custom/ErrorPage';
import LoadingSpinner from '../LoadingSpinner';
import { useImpersonating } from '../Hooks/useImpersonating';
import { savePdfResponse } from '../utils/billingHelpers';
import MemberCommentThread, {
  MemberThreadSource,
} from '../Violations/comments/MemberCommentThread';
import CancelReservationDialog from './CancelReservationDialog';
import RescheduleDialog from './RescheduleDialog';
import {
  calendarFileName,
  canCancel,
  canReschedule,
  endedByText,
  feeStatusText,
  formatLocalDateTime,
  formatLocalRange,
  isFreeAmount,
  nightsLabel,
  nowLocalIn,
  statusColor,
  statusLabel,
  timesAreIn,
} from './reservationFormat';
import { reservationErrorMessage } from './ruleMessages';
import {
  accessLaterText,
  accessView,
  depositStatusText,
  feeLineAmount,
  formatEpochTime,
  showFeeBreakdown,
} from './reservationPhase3';
import ReservationDocumentsSection from './ReservationDocumentsSection';

/** What the status means for the homeowner, in a sentence. */
const statusExplanation = (r: ReservationReturn): string => {
  const endedBy = endedByText(r);
  if (endedBy) return endedBy;
  switch (r.status) {
    case 'pending':
      return r.documents_outstanding && r.documents_outstanding > 0
        ? `Waiting on ${r.documents_outstanding === 1 ? 'a document' : `${r.documents_outstanding} documents`} before your management company can approve this request — upload ${r.documents_outstanding === 1 ? 'it' : 'them'} below.`
        : "Your management company hasn't decided on this request yet. We'll let you know when they do.";
    case 'approved':
      return "You're booked.";
    case 'denied':
      return 'Your management company declined this request.';
    case 'cancelled':
      return 'This booking was cancelled.';
    case 'expired':
      return 'This request expired before it was approved, so the time was released.';
    case 'completed':
      return 'This booking has finished.';
    case 'no_show':
      return 'Your management company recorded that no one from your household used this booking. If that is wrong, send them a message below.';
    default:
      return '';
  }
};

const ACTIVE = ['pending', 'approved'];

const ReservationDetailPage: React.FC = () => {
  const { reservationId } = useParams<{ reservationId: string }>();
  const impersonating = useImpersonating();
  const [reservation, setReservation] = useState<ReservationReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moved, setMoved] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  // Bumped by "Try again" to re-run the load below.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!reservationId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    memberReservationService
      .getMyReservation({ reservationId })
      .then(({ data }) => {
        if (!cancelled) setReservation(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setReservation(null);
          setError(reservationErrorMessage(err, "Couldn't load this reservation."));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reservationId, reloadKey]);

  const r = reservation;
  const now = r ? nowLocalIn(r.timezone) : '';
  const access = r ? accessView(r) : ({ kind: 'none' } as const);

  const addToCalendar = async (): Promise<void> => {
    if (!r) return;
    setCalendarError(null);
    try {
      const response = await memberReservationService.getMyReservationCalendar(
        { reservationId: r.id },
        { responseType: 'blob' as const }
      );
      savePdfResponse(response, calendarFileName(r));
    } catch (err) {
      setCalendarError(reservationErrorMessage(err, "Couldn't download the calendar file."));
    }
  };

  const thread: MemberThreadSource | null = r
    ? {
        list: async () => {
          const { data } = await memberReservationService.listMyReservationComments({
            reservationId: r.id,
          });
          return data.comments ?? [];
        },
        post: (content, parentId) =>
          memberReservationService.createMyReservationComment({
            reservationId: r.id,
            memberReservationCommentCreate: {
              content,
              ...(parentId ? { parent_id: parentId } : {}),
            },
          }),
        // Editing and deleting a member's own comment is the one generic
        // route, whatever the comment hangs off.
        remove: (commentId) => memberCommentService.deleteMyViolationComment({ commentId }),
      }
    : null;

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="reservations" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <Link
        to="/reservations"
        className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:underline dark:text-zinc-400"
      >
        <ChevronLeftIcon className="h-4 w-4" aria-hidden />
        My reservations
      </Link>

      {loading ? (
        <LoadingSpinner />
      ) : error || !r ? (
        <ErrorPage
          title="Couldn't load this reservation"
          message={error ?? "We couldn't find that reservation."}
          onRetry={error ? () => setReloadKey((k) => k + 1) : undefined}
        />
      ) : (
        <div className="mt-4 max-w-2xl space-y-6">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <Heading>{r.amenity_name}</Heading>
              <Badge color={statusColor(r.status)}>{statusLabel(r.status)}</Badge>
            </div>
            <Text className="mt-1">{formatLocalRange(r.start_local, r.end_local)}</Text>
            <Text className="mt-1 text-xs">{timesAreIn(r.timezone)}</Text>
          </div>

          <Text>{statusExplanation(r)}</Text>
          {moved && (
            <Text className="rounded-lg bg-green-50 p-3 text-sm text-green-900 dark:bg-green-500/10 dark:text-green-200">
              Moved — your booking is now {formatLocalRange(r.start_local, r.end_local)}.
            </Text>
          )}

          {r.decision_note && (
            <section className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-700 dark:bg-gray-800">
              <Subheading>Note from your management company</Subheading>
              <Text className="mt-2 whitespace-pre-wrap">{r.decision_note}</Text>
            </section>
          )}

          <section className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-700 dark:bg-gray-800">
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-[auto_1fr] sm:gap-x-6">
              <dt className="font-medium text-zinc-500 dark:text-zinc-400">Home</dt>
              <dd className="text-zinc-950 dark:text-white">{r.home_address}</dd>
              {r.nights ? (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">Stay</dt>
                  <dd className="text-zinc-950 dark:text-white">
                    {nightsLabel(r.nights)} · check in {formatLocalDateTime(r.start_local)}, check out{' '}
                    {formatLocalDateTime(r.end_local)}
                  </dd>
                </>
              ) : null}
              <dt className="font-medium text-zinc-500 dark:text-zinc-400">People</dt>
              <dd className="text-zinc-950 dark:text-white">{r.party_size}</dd>
              {r.vehicle_plate && (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">Vehicle</dt>
                  <dd className="text-zinc-950 dark:text-white">
                    {[r.vehicle_plate, r.vehicle_state].filter(Boolean).join(' · ')}
                    {r.vehicle_description ? ` — ${r.vehicle_description}` : ''}
                  </dd>
                </>
              )}
              {r.purpose && (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">For</dt>
                  <dd className="text-zinc-950 dark:text-white">{r.purpose}</dd>
                </>
              )}
              <dt className="font-medium text-zinc-500 dark:text-zinc-400">Fee</dt>
              <dd className="text-zinc-950 dark:text-white">
                {feeStatusText(r)}
                {showFeeBreakdown(r.fee_breakdown) && (
                  <ul className="mt-1 space-y-0.5 text-zinc-600 dark:text-zinc-400">
                    {r.fee_breakdown.map((line, i) => (
                      <li key={`${line.kind}-${i}`} className="flex justify-between gap-4">
                        <span className="min-w-0">{line.label}</span>
                        <span className="shrink-0 tabular-nums">{feeLineAmount(line)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </dd>
              {depositStatusText(r) && (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">Deposit</dt>
                  <dd className="text-zinc-950 dark:text-white">{depositStatusText(r)}</dd>
                </>
              )}
              {r.checked_in_at ? (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">Checked in</dt>
                  <dd className="text-zinc-950 dark:text-white">{formatEpochTime(r.checked_in_at, r.timezone)}</dd>
                </>
              ) : null}
              {!isFreeAmount(r.fee_amount) &&
                r.fee_refundable_until_local &&
                ACTIVE.indexOf(r.status) !== -1 && (
                  <>
                    <dt className="font-medium text-zinc-500 dark:text-zinc-400">
                      Free cancellation
                    </dt>
                    <dd className="text-zinc-950 dark:text-white">
                      Until {formatLocalDateTime(r.fee_refundable_until_local)}
                    </dd>
                  </>
                )}
              {r.agreement_version && (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">Agreement</dt>
                  <dd className="text-zinc-950 dark:text-white">
                    Version {r.agreement_version} accepted
                  </dd>
                </>
              )}
              {r.cancel_reason && (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">
                    Cancellation reason
                  </dt>
                  <dd className="text-zinc-950 dark:text-white">{r.cancel_reason}</dd>
                </>
              )}
            </dl>
          </section>

          {access.kind !== 'none' && (
            <section className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-700 dark:bg-gray-800">
              <Subheading>Getting in</Subheading>
              {access.kind === 'later' ? (
                <Text className="mt-2">{accessLaterText(access.from)}</Text>
              ) : (
                <>
                  {access.code && (
                    <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
                      Access code{' '}
                      <span className="ml-1 rounded-md bg-zinc-100 px-2 py-1 font-mono text-lg font-semibold tracking-widest text-zinc-950 dark:bg-zinc-700 dark:text-white">
                        {access.code}
                      </span>
                    </p>
                  )}
                  {access.instructions && (
                    <Text className="mt-2 whitespace-pre-wrap">{access.instructions}</Text>
                  )}
                </>
              )}
            </section>
          )}

          <ReservationDocumentsSection reservation={r} />

          {ACTIVE.indexOf(r.status) !== -1 && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-3">
                {canReschedule(r, now) && (
                  <Button outline onClick={() => setMoveOpen(true)}>
                    Change time
                  </Button>
                )}
                <Button outline onClick={addToCalendar}>
                  Add to calendar
                </Button>
                {canCancel(r, now) && (
                  <Button outline onClick={() => setCancelOpen(true)} disabled={impersonating}>
                    {r.status === 'pending' ? 'Withdraw request' : 'Cancel booking'}
                  </Button>
                )}
              </div>
              {impersonating && (
                <Text className="text-sm">
                  This is a read-only view: you can preview a new time, but the booking can't be
                  moved or cancelled here.
                </Text>
              )}
              {!canReschedule(r, now) && canCancel(r, now) && (
                <Text className="text-sm">
                  It's past the cancellation deadline, so the time can't be changed now.
                </Text>
              )}
              <ErrorAlert onDismiss={() => setCalendarError(null)}>
                {calendarError}
              </ErrorAlert>
            </div>
          )}

          {thread && (
            <MemberCommentThread
              source={thread}
              sourceKey={r.id}
              title="Messages with your management company"
              emptyText="No messages yet. Write here if you have a question about this booking."
              sendLabel="Send to your community manager"
              otherMemberLabel="Someone in your household"
              readOnlyReason={
                impersonating ? "This is a read-only view, so messages can't be sent here." : null
              }
            />
          )}
        </div>
      )}

      <CancelReservationDialog
        reservation={r}
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onSuccess={(updated) => {
          setReservation(updated);
          setCancelOpen(false);
          setMoved(false);
        }}
      />
      {r && (
        <RescheduleDialog
          reservation={r}
          isOpen={moveOpen}
          onClose={() => setMoveOpen(false)}
          onSuccess={(updated) => {
            setReservation(updated);
            setMoveOpen(false);
            setMoved(true);
          }}
        />
      )}
    </SidebarLayout>
  );
};

export default ReservationDetailPage;
